import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Brain, Activity, Shield, TrendingUp, TrendingDown, MinusCircle,
  Zap, AlertTriangle, PauseCircle, XCircle, CheckCircle2, Loader2,
} from "lucide-react";
import { api } from "@/lib/api";

const REGIME_META = {
  trending_up:     { label: "TRENDING UP",     color: "#10b981", Icon: TrendingUp },
  trending_down:   { label: "TRENDING DOWN",   color: "#ef4444", Icon: TrendingDown },
  ranging:         { label: "RANGING",         color: "#3b82f6", Icon: MinusCircle },
  high_volatility: { label: "HIGH VOLATILITY", color: "#f59e0b", Icon: Zap },
  low_liquidity:   { label: "LOW LIQUIDITY",   color: "#6b7280", Icon: PauseCircle },
  news_window:     { label: "NEWS WINDOW",     color: "#a855f7", Icon: AlertTriangle },
  unknown:         { label: "COLLECTING",      color: "#6b7280", Icon: Loader2 },
};

const LIGHT_STYLES = {
  green: { bg: "bg-[#10b981]/10",  border: "border-[#10b981]/40",  text: "text-[#10b981]" },
  amber: { bg: "bg-[#f59e0b]/10",  border: "border-[#f59e0b]/40",  text: "text-[#f59e0b]" },
  red:   { bg: "bg-[#ef4444]/10",  border: "border-[#ef4444]/40",  text: "text-[#ef4444]" },
};

const VERDICT_STYLES = {
  APPROVED: { text: "text-[#10b981]", bg: "bg-[#10b981]/10", label: "APPROVED",  Icon: CheckCircle2 },
  REDUCED:  { text: "text-[#f59e0b]", bg: "bg-[#f59e0b]/10", label: "REDUCED",   Icon: MinusCircle },
  SKIPPED:  { text: "text-[#ef4444]", bg: "bg-[#ef4444]/10", label: "SKIPPED",   Icon: XCircle },
};

const HealthGauge = ({ score }) => {
  const s = Math.max(0, Math.min(100, score || 0));
  const color = s >= 85 ? "#10b981" : s >= 70 ? "#3b82f6" : s >= 50 ? "#f59e0b" : "#ef4444";
  const r = 54, c = 2 * Math.PI * r;
  const off = c * (1 - s / 100);
  return (
    <div className="relative w-32 h-32" data-testid="ati-health-gauge">
      <svg viewBox="0 0 128 128" className="w-32 h-32 -rotate-90">
        <circle cx="64" cy="64" r={r} stroke="#1f2937" strokeWidth="10" fill="none" />
        <circle cx="64" cy="64" r={r} stroke={color} strokeWidth="10" fill="none"
                strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round"
                style={{ transition: "stroke-dashoffset 0.6s ease" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="font-heading font-black text-3xl" style={{ color }}>{s}</div>
        <div className="text-[9px] font-mono tracking-widest text-[#9ca3af] mt-0.5">/ 100</div>
      </div>
    </div>
  );
};

const SymbolCard = ({ item }) => {
  const meta = REGIME_META[item.regime] || REGIME_META.unknown;
  const light = LIGHT_STYLES[item.traffic_light] || LIGHT_STYLES.amber;
  const Icon = meta.Icon;
  const lotPct = Math.round((item.lot_multiplier ?? 0) * 100);
  return (
    <div
      className="rounded-lg border border-[#1f2937] bg-[#111827] p-4 space-y-3 hover:border-[#3b82f6]/50 transition-colors"
      data-testid={`ati-symbol-card-${item.symbol}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="font-mono font-bold text-lg tracking-tight">{item.symbol}</div>
          <div className="text-[10px] font-mono text-[#6b7280] mt-0.5">
            {item.session || "—"} · ATR {(item.atr || 0).toFixed(5)}
          </div>
        </div>
        <div className={`w-3 h-3 rounded-full ${light.bg} border ${light.border}`}
             data-testid={`ati-light-${item.symbol}`} />
      </div>

      <div className={`flex items-center gap-2 rounded-md border ${light.border} ${light.bg} px-3 py-2`}>
        <Icon className={`w-4 h-4 ${light.text}`} strokeWidth={2} />
        <div className="flex-1 min-w-0">
          <div className={`text-[10px] font-mono tracking-widest ${light.text} font-bold`}>{meta.label}</div>
          <div className="text-[10px] text-[#9ca3af] truncate">{item.regime_reason}</div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <div className="text-[9px] font-mono tracking-widest text-[#6b7280]">ACTION</div>
          <div className={`font-bold text-sm ${light.text}`}>{item.action}</div>
        </div>
        <div className="text-right">
          <div className="text-[9px] font-mono tracking-widest text-[#6b7280]">LOT SIZE</div>
          <div className="font-bold text-sm">{lotPct}%</div>
        </div>
      </div>

      {item.top_reasons && item.top_reasons.length > 0 && (
        <div className="pt-2 border-t border-[#1f2937] space-y-1">
          {item.top_reasons.map((r, i) => (
            <div key={i} className="text-[10px] text-[#9ca3af] leading-relaxed">· {r}</div>
          ))}
        </div>
      )}
    </div>
  );
};

const DecisionRow = ({ d }) => {
  const v = VERDICT_STYLES[d.ati_decision?.verdict] || VERDICT_STYLES.APPROVED;
  const VIcon = v.Icon;
  const created = d.created_at ? new Date(d.created_at).toLocaleString() : "";
  return (
    <div
      className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-[#1f2937] text-sm hover:bg-[#0f1626]"
      data-testid="ati-decision-row"
    >
      <div className="col-span-2 font-mono text-xs text-[#9ca3af]">{created}</div>
      <div className="col-span-1 font-mono font-bold">{d.symbol}</div>
      <div className="col-span-1 font-mono text-xs text-[#6b7280]">{d.timeframe}</div>
      <div className="col-span-1 text-xs">{d.pattern_type}</div>
      <div className={`col-span-1 text-xs font-bold ${d.direction === "BUY" ? "text-[#10b981]" : "text-[#ef4444]"}`}>
        {d.direction}
      </div>
      <div className="col-span-1 font-mono text-xs">{d.confidence_score}</div>
      <div className="col-span-1">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold ${v.bg} ${v.text}`}>
          <VIcon className="w-3 h-3" /> {v.label}
        </span>
      </div>
      <div className="col-span-1 font-mono text-xs">{Math.round((d.ati_decision?.lot_multiplier ?? 0) * 100)}%</div>
      <div className="col-span-3 text-xs text-[#9ca3af] truncate" title={d.ati_decision?.narrative}>
        {d.ati_decision?.narrative}
      </div>
    </div>
  );
};

export default function AutonomousIntelligence() {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [health, setHealth] = useState(null);
  const [decisions, setDecisions] = useState([]);

  const load = useCallback(async () => {
    try {
      const [s, h, d] = await Promise.all([
        api.get("/ati/status"),
        api.get("/ati/health"),
        api.get("/ati/decisions?limit=30"),
      ]);
      setStatus(s.data);
      setHealth(h.data);
      setDecisions(d.data.decisions || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load ATI");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [load]);

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[50vh]" data-testid="ati-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#3b82f6]" />
      </div>
    );
  }

  const symbols = status?.symbols || [];
  const green = symbols.filter(s => s.traffic_light === "green").length;
  const amber = symbols.filter(s => s.traffic_light === "amber").length;
  const red   = symbols.filter(s => s.traffic_light === "red").length;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="ati-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="vx-label mb-1">Autonomous Trading Intelligence</div>
          <h1 className="font-heading text-3xl lg:text-4xl font-black tracking-tighter">
            AI Decision Engine
          </h1>
          <p className="text-sm text-[#9ca3af] mt-2 max-w-2xl leading-relaxed">
            We don&apos;t optimize for winning trades. We optimize for surviving markets.
            Every setup is scored on market regime, pattern fit, and account health BEFORE
            it reaches your MT5 terminal.
          </p>
        </div>
        <button
          onClick={load}
          className="text-[10px] font-mono uppercase tracking-widest text-[#9ca3af] hover:text-[#3b82f6] transition-colors"
          data-testid="ati-refresh-btn"
        >
          ↻ Refresh
        </button>
      </div>

      {/* Health + light summary */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-1 rounded-lg border border-[#1f2937] bg-[#111827] p-6 flex flex-col items-center justify-center gap-3"
             data-testid="ati-health-card">
          <div className="vx-label">Account Health</div>
          <HealthGauge score={health?.health_score} />
          <div className="text-[10px] text-[#9ca3af] text-center leading-relaxed max-w-[220px]">
            {health?.narrative}
          </div>
        </div>

        <div className="lg:col-span-3 grid grid-cols-3 gap-4">
          <div className="rounded-lg border border-[#10b981]/40 bg-[#10b981]/5 p-5" data-testid="ati-count-green">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 rounded-full bg-[#10b981]" />
              <div className="vx-label text-[#10b981]">Green — Trade</div>
            </div>
            <div className="font-heading text-4xl font-black text-[#10b981]">{green}</div>
            <div className="text-[10px] text-[#9ca3af] mt-1">symbols cleared at full size</div>
          </div>
          <div className="rounded-lg border border-[#f59e0b]/40 bg-[#f59e0b]/5 p-5" data-testid="ati-count-amber">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 rounded-full bg-[#f59e0b]" />
              <div className="vx-label text-[#f59e0b]">Amber — Reduce</div>
            </div>
            <div className="font-heading text-4xl font-black text-[#f59e0b]">{amber}</div>
            <div className="text-[10px] text-[#9ca3af] mt-1">symbols scaled down 50%</div>
          </div>
          <div className="rounded-lg border border-[#ef4444]/40 bg-[#ef4444]/5 p-5" data-testid="ati-count-red">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 rounded-full bg-[#ef4444]" />
              <div className="vx-label text-[#ef4444]">Red — Skip</div>
            </div>
            <div className="font-heading text-4xl font-black text-[#ef4444]">{red}</div>
            <div className="text-[10px] text-[#9ca3af] mt-1">symbols paused (capital-first)</div>
          </div>
        </div>
      </div>

      {/* Health breakdown */}
      {health?.breakdown && (
        <div className="rounded-lg border border-[#1f2937] bg-[#111827] p-5" data-testid="ati-health-breakdown">
          <div className="vx-label mb-3 flex items-center gap-2">
            <Shield className="w-3 h-3" /> Health Breakdown
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {Object.entries(health.breakdown).map(([k, v]) => (
              <div key={k} className="text-center">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#6b7280] mb-1">
                  {k.replace(/_/g, " ")}
                </div>
                <div className="font-heading text-2xl font-black" style={{
                  color: v >= 85 ? "#10b981" : v >= 70 ? "#3b82f6" : v >= 50 ? "#f59e0b" : "#ef4444"
                }}>
                  {v}
                </div>
                <div className="h-1 bg-[#1f2937] rounded-full overflow-hidden mt-1">
                  <div className="h-full rounded-full transition-all duration-500" style={{
                    width: `${v}%`,
                    background: v >= 85 ? "#10b981" : v >= 70 ? "#3b82f6" : v >= 50 ? "#f59e0b" : "#ef4444",
                  }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Per-symbol regime cards */}
      <div>
        <div className="vx-label mb-3 flex items-center gap-2">
          <Activity className="w-3 h-3" /> Per-Symbol Intelligence
        </div>
        {symbols.length === 0 ? (
          <div className="rounded-lg border border-[#1f2937] bg-[#111827] p-8 text-center text-sm text-[#9ca3af]"
               data-testid="ati-no-symbols">
            {status?.note || "No symbol data yet."}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {symbols.map(s => <SymbolCard key={s.symbol} item={s} />)}
          </div>
        )}
      </div>

      {/* Decisions log */}
      <div className="rounded-lg border border-[#1f2937] bg-[#111827]" data-testid="ati-decisions-log">
        <div className="px-4 py-3 border-b border-[#1f2937] flex items-center justify-between">
          <div className="vx-label flex items-center gap-2">
            <Brain className="w-3 h-3" /> Recent Decisions
          </div>
          <div className="text-[10px] font-mono text-[#6b7280]">{decisions.length} entries</div>
        </div>
        <div className="grid grid-cols-12 gap-3 px-4 py-2 border-b border-[#1f2937] text-[9px] font-mono tracking-widest uppercase text-[#6b7280]">
          <div className="col-span-2">Time</div>
          <div className="col-span-1">Symbol</div>
          <div className="col-span-1">TF</div>
          <div className="col-span-1">Pattern</div>
          <div className="col-span-1">Dir</div>
          <div className="col-span-1">Conf</div>
          <div className="col-span-1">Verdict</div>
          <div className="col-span-1">Lot</div>
          <div className="col-span-3">Narrative</div>
        </div>
        {decisions.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-[#9ca3af]">
            No decisions yet — ATI will log every signal it evaluates here.
          </div>
        ) : (
          decisions.map(d => <DecisionRow key={d.id} d={d} />)
        )}
      </div>
    </div>
  );
}
